/**
 * Tooltip Component
 * Desktop: hover to show. Mobile: tap info icon to toggle.
 * Uses React Portal with position:fixed to escape all containers.
 * Pure Tailwind, zero dependencies.
 */

import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  /** Position preference (auto-flips if near edge) */
  position?: 'top' | 'bottom';
}

export const Tooltip: React.FC<TooltipProps> = ({ text, children, position = 'top' }) => {
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [actualPos, setActualPos] = useState(position);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();

  // Detect mobile (touch-primary device)
  useEffect(() => {
    const mq = window.matchMedia('(pointer: coarse)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Calculate position when tooltip becomes visible
  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const tooltipHeight = 60; // estimated max height
    const centerX = rect.left + rect.width / 2;

    let pos = position;
    // Auto-flip if too close to edge
    if (position === 'top' && rect.top < tooltipHeight + 16) {
      pos = 'bottom';
    } else if (position === 'bottom' && rect.bottom > window.innerHeight - tooltipHeight - 16) {
      pos = 'top';
    }
    setActualPos(pos);

    // Use viewport coordinates directly — position:fixed uses viewport as reference
    const top = pos === 'top'
      ? rect.top - 8
      : rect.bottom + 8;

    // Clamp horizontally to stay within viewport
    const clampedLeft = Math.max(130, Math.min(centerX, window.innerWidth - 130));

    setCoords({ top, left: clampedLeft });
  }, [position]);

  useEffect(() => {
    if (visible) updatePosition();
  }, [visible, updatePosition]);

  // Close on outside click (mobile)
  useEffect(() => {
    if (!visible || !isMobile) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setVisible(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [visible, isMobile]);

  // Desktop: hover handlers with delay
  const handleMouseEnter = useCallback(() => {
    if (isMobile) return;
    timeoutRef.current = setTimeout(() => setVisible(true), 300);
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (isMobile) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setVisible(false);
  }, [isMobile]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, []);

  const tooltipEl = visible
    ? createPortal(
        <div
          id={tooltipId}
          role="tooltip"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            transform: actualPos === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
          }}
          className="z-[9999] max-w-[240px] px-3 py-2 bg-slate-800 text-white text-[11px] leading-relaxed font-medium rounded-lg shadow-lg pointer-events-none whitespace-normal"
        >
          {text}
          {/* Arrow */}
          <div
            className={`absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-800 rotate-45 ${
              actualPos === 'top' ? '-bottom-1' : '-top-1'
            }`}
          />
        </div>,
        document.body
      )
    : null;

  // Use <span> instead of <div> so it works inside <button>, <p>, etc. without
  // violating HTML nesting rules (div inside button is technically invalid).
  return (
    <span
      ref={containerRef}
      className="inline-flex items-center gap-1"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-describedby={visible ? tooltipId : undefined}
    >
      {children}

      {/* Mobile: info icon trigger */}
      {isMobile && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setVisible((v) => !v); }}
          className="shrink-0 w-4 h-4 rounded-full bg-slate-200 text-slate-500 text-[9px] font-bold flex items-center justify-center hover:bg-slate-300 transition-colors"
          aria-label="More info"
          aria-expanded={visible}
          aria-controls={tooltipId}
        >
          i
        </button>
      )}

      {tooltipEl}
    </span>
  );
};
